import { useState, useCallback, useEffect } from 'react';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import { userSetting as userSettingsApi } from 'src/api/user-setting/user-setting';
import { UserSettingsType } from 'src/contexts/UserSettingsContext/user-settings.type';
/**
 * Custom hook for managing user settings state and actions.
 * Provides functions to set user settings.
 */
export function useUserSettings() {
  const [userSettingsState, setUserSettingsState] = useState<UserSettingsType | null>(null);
  const [userSettingsLoaded, setUserSettingsLoaded] = useState(false);

  // Internal stable function to fetch data and set initial loaded state
  const fetchAndInitializeSettings = useCallback(async () => {
    try {
      const response = await userSettingsApi.fetchUserSettings();
      setUserSettingsState(response);
      setUserSettingsLoaded(true); // Mark as loaded after the first successful fetch
    } catch (error) {
      console.error('Failed to fetch initial user settings:', error);
      setUserSettingsLoaded(true);
    }
  }, []); // Stable: no dependencies from the hook's scope

  // Effect for initial load: fetch settings if not already loaded
  useEffect(() => {
    if (!userSettingsLoaded) {
      void fetchAndInitializeSettings();
    }
  }, [userSettingsLoaded, fetchAndInitializeSettings]);

  // Exposed function for manual refresh - this will be the new getUserSettings
  const refreshUserSettings = useCallback(async () => {
    try {
      const response = await userSettingsApi.fetchUserSettings();
      setUserSettingsState(response);
    } catch (error) {
      console.error('Failed to refresh user settings:', error);
    }
  }, []);

  const setWithdrawalOptionSelected = useCallback(
    (introRewardSelectionCompletion: boolean) => {
      if (userSettingsState) {
        setUserSettingsState({
          ...userSettingsState,
          introRewardSelectionCompletion,
        });
      }
    },
    [userSettingsState]
  );

  const setFirstDynamicProfilerCompletion = useCallback(
    (firstDynamicProfilerCompletion: boolean) => {
      if (userSettingsState) {
        setUserSettingsState({
          ...userSettingsState,
          firstDynamicProfilerCompletion,
        });
      }
    },
    [userSettingsState]
  );

  const setProfileSurveyCompletion = useCallback((profileSurveyCompletion: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), profileSurveyCompletion }) as UserSettingsType);
  }, []);

  const setSurveyIntroCompletion = useCallback(
    (surveyIntroCompletion: boolean) => {
      if (userSettingsState) {
        setUserSettingsState({
          ...userSettingsState,
          surveyIntroCompletion,
        });
      }
    },
    [userSettingsState]
  );

  const setWelcomeModalAcknowledgement = useCallback((welcomeModalAcknowledgement: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), welcomeModalAcknowledgement }) as UserSettingsType);
  }, []);

  const setPostRegistrationFormCompletion = useCallback((postRegistrationFormCompletion: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), postRegistrationFormCompletion }) as UserSettingsType);
  }, []);

  const setHapticsEnabled = useCallback((hapticsEnabled: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), hapticsEnabled }) as UserSettingsType);
  }, []);

  const setShowGamesOnboarding = useCallback((showGamesOnboarding: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), showGamesOnboarding }) as UserSettingsType);
  }, []);

  const setShowLevelUpModalFromGames = useCallback((showLevelUpModalFromGames: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), showLevelUpModalFromGames }) as UserSettingsType);
  }, []);

  const setGamesPlatformOsTypeId = useCallback((gamesPlatformOsTypeId: number | null) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), gamesPlatformOsTypeId }) as UserSettingsType);
  }, []);

  const setBrowserNotificationsEnabled = useCallback((browserNotificationsEnabled: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), browserNotificationsEnabled }) as UserSettingsType);
  }, []);

  const setBrowserNotificationsPromptDismissed = useCallback((browserNotificationsPromptDismissed: boolean) => {
    setUserSettingsState(
      (prevState) => ({ ...(prevState || {}), browserNotificationsPromptDismissed }) as UserSettingsType
    );
  }, []);

  const setUnreadGameInviteCount = useCallback((unreadGameInviteCount: number) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), unreadGameInviteCount }) as UserSettingsType);
  }, []);

  const setUnreadProjectInviteCount = useCallback((unreadProjectInviteCount: number) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), unreadProjectInviteCount }) as UserSettingsType);
  }, []);

  const setShowPointsToCashConversionNotice = useCallback((showPointsToCashConversionNotice: boolean) => {
    setUserSettingsState(
      (prevState) => ({ ...(prevState || {}), showPointsToCashConversionNotice }) as UserSettingsType
    );
  }, []);

  // [DELETE IN PROD-790]
  const setNotifyAboutNewLevelingUpSystem = useCallback((notifyAboutNewLevelingUpSystem: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), notifyAboutNewLevelingUpSystem }) as UserSettingsType);
  }, []);
  // [END OF DELETION]

  // [DELETE IN PROD-792]
  const setNotifyAboutNewStreakSystem = useCallback((notifyAboutNewStreakSystem: boolean) => {
    setUserSettingsState((prevState) => ({ ...(prevState || {}), notifyAboutNewStreakSystem }) as UserSettingsType);
  }, []);
  // [END OF DELETION]

  const saveUserSettings = useCallback(
    async (settingType: UserSettingType, value: string | null) => {
      try {
        await userSettingsApi.createOrUpdateUserSetting({ settingType, value });
        // After saving to DB, update local state immediately
        switch (settingType) {
          case UserSettingType.IntroRewardSelectionCompletion:
            setWithdrawalOptionSelected(!!value);
            break;
          case UserSettingType.WelcomeModalAcknowledgement:
            setWelcomeModalAcknowledgement(!!value);
            break;
          case UserSettingType.SurveyIntroCompletion:
            setSurveyIntroCompletion(!!value);
            break;
          case UserSettingType.ProfileSurveyCompletion:
            setProfileSurveyCompletion(!!value);
            break;
          case UserSettingType.HapticsEnabled:
            setHapticsEnabled(value !== 'false');
            break;
          case UserSettingType.ShowGamesOnboarding:
            setShowGamesOnboarding(value === 'true');
            break;
          case UserSettingType.ShowLevelUpModalFromGames:
            setShowLevelUpModalFromGames(value === 'true');
            break;
          case UserSettingType.GamesPlatformOsType:
            setGamesPlatformOsTypeId(value == null ? null : Number(value));
            break;
          case UserSettingType.BrowserNotificationsEnabled:
            setBrowserNotificationsEnabled(value === 'true');
            break;
          case UserSettingType.BrowserNotificationsPromptDismissed:
            setBrowserNotificationsPromptDismissed(value === 'true');
            break;
          case UserSettingType.UnreadGameInviteCount:
            setUnreadGameInviteCount(Math.max(0, Number(value) || 0));
            break;
          case UserSettingType.UnreadProjectInviteCount:
            setUnreadProjectInviteCount(Math.max(0, Number(value) || 0));
            break;
          case UserSettingType.ShowPointsToCashConversionNotice:
            setShowPointsToCashConversionNotice(value === 'true');
            break;
          // [DELETE IN PROD-790]
          case UserSettingType.NotifyAboutNewLevelingUpSystem:
            setNotifyAboutNewLevelingUpSystem(value === 'true');
            break;
          // [END OF DELETION]
          // [DELETE IN PROD-792]
          case UserSettingType.NotifyAboutNewStreakSystem:
            setNotifyAboutNewStreakSystem(value === 'true');
            break;
          // [END OF DELETION]
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      setBrowserNotificationsEnabled,
      setBrowserNotificationsPromptDismissed,
      setGamesPlatformOsTypeId,
      setHapticsEnabled,
      setNotifyAboutNewLevelingUpSystem,
      setNotifyAboutNewStreakSystem,
      setProfileSurveyCompletion,
      setShowGamesOnboarding,
      setShowLevelUpModalFromGames,
      setSurveyIntroCompletion,
      setShowPointsToCashConversionNotice,
      setUnreadGameInviteCount,
      setUnreadProjectInviteCount,
      setWelcomeModalAcknowledgement,
      setWithdrawalOptionSelected,
    ]
  );

  return {
    userSettings: userSettingsState,
    userSettingsLoaded,
    setWithdrawalOptionSelected,
    setSurveyIntroCompletion,
    setWelcomeModalAcknowledgement,
    setPostRegistrationFormCompletion,
    setFirstDynamicProfilerCompletion,
    setProfileSurveyCompletion,
    setHapticsEnabled,
    setShowGamesOnboarding,
    setShowLevelUpModalFromGames,
    setGamesPlatformOsTypeId,
    setBrowserNotificationsEnabled,
    setBrowserNotificationsPromptDismissed,
    setUnreadGameInviteCount,
    setUnreadProjectInviteCount,
    setShowPointsToCashConversionNotice,
    saveUserSettings,
    refreshUserSettings,

    // [DELETE IN PROD-790]
    setNotifyAboutNewLevelingUpSystem,
    // [END OF DELETION]

    // [DELETE IN PROD-792]
    setNotifyAboutNewStreakSystem,
    // [END OF DELETION]
  };
}
